import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Moon, TrendingUp, TrendingDown, RefreshCw } from "lucide-react";
import { API } from "../config/api";

/**
 * Compact premarket high / low / open / last card for the Stock Analysis page.
 * Pulls from /api/stock/premarket-snapshot (Yahoo v8 prepost data, no
 * yfinance, 1-min cache). Refreshes itself every 60s during the trading day.
 *
 * Renders nothing if the backend reports no premarket bars (e.g. weekends,
 * extremely thin tickers, or pre-04:00 ET).
 */
export default function PremarketHighLowCard({ ticker }) {
  const [snap, setSnap] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetch = useCallback(async () => {
    if (!ticker) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/stock/premarket-snapshot/${ticker}`,
                                { timeout: 12000 });
      setSnap(r.data);
    } catch {
      // silent — optional widget
    } finally {
      setLoading(false);
    }
  }, [ticker]);

  useEffect(() => { fetch(); }, [fetch]);
  useEffect(() => {
    const id = setInterval(fetch, 60000);
    return () => clearInterval(id);
  }, [fetch]);

  if (!snap || !snap.ready) return null;

  const chg = snap.change_pct_vs_prev_close;
  const chgColor = chg == null
    ? "text-slate-400"
    : chg >= 0
    ? "text-emerald-400"
    : "text-red-400";

  return (
    <div className="bg-slate-900/80 rounded-lg border border-slate-800 p-3"
         data-testid="premarket-highlow-card">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Moon className="w-4 h-4 text-indigo-400" />
          <span className="text-white font-semibold text-sm">
            Premarket — {ticker}
          </span>
          <span className="text-[10px] text-slate-500">
            {snap.date} · {snap.bars} bars · 04:00–09:30 ET
          </span>
        </div>
        <button
          onClick={fetch}
          disabled={loading}
          data-testid="premarket-refresh"
          className="text-slate-400 hover:text-white p-1 disabled:opacity-50"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {/* High */}
        <div className="rounded-md border border-emerald-900/60 bg-emerald-950/30 p-2"
             data-testid="premarket-high">
          <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-emerald-400 font-semibold">
            <TrendingUp className="w-3 h-3" />
            Premarket High
          </div>
          <div className="font-mono text-lg font-bold text-emerald-300 mt-0.5">
            ${snap.premarket_high?.toFixed(2)}
          </div>
          {snap.high_time && (
            <div className="text-[10px] text-slate-500">@ {snap.high_time}</div>
          )}
        </div>

        {/* Low */}
        <div className="rounded-md border border-red-900/60 bg-red-950/30 p-2"
             data-testid="premarket-low">
          <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-red-400 font-semibold">
            <TrendingDown className="w-3 h-3" />
            Premarket Low
          </div>
          <div className="font-mono text-lg font-bold text-red-300 mt-0.5">
            ${snap.premarket_low?.toFixed(2)}
          </div>
          {snap.low_time && (
            <div className="text-[10px] text-slate-500">@ {snap.low_time}</div>
          )}
        </div>

        {/* Open */}
        <div className="rounded-md border border-slate-800 bg-slate-900/60 p-2"
             data-testid="premarket-open">
          <div className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">
            04:00 Open
          </div>
          <div className="font-mono text-lg font-bold text-slate-200 mt-0.5">
            ${snap.premarket_open?.toFixed(2)}
          </div>
          <div className="text-[10px] text-slate-500">
            Prev close ${snap.previous_close?.toFixed(2)}
          </div>
        </div>

        {/* Last / change vs prev close */}
        <div className="rounded-md border border-slate-800 bg-slate-900/60 p-2"
             data-testid="premarket-last">
          <div className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">
            Last (vs prev close)
          </div>
          <div className="font-mono text-lg font-bold text-slate-200 mt-0.5">
            ${snap.premarket_last?.toFixed(2)}
          </div>
          {chg != null && (
            <div className={`text-[11px] font-semibold ${chgColor}`}>
              {chg >= 0 ? "+" : ""}{chg.toFixed(2)}% (${snap.change_vs_prev_close >= 0 ? "+" : ""}{snap.change_vs_prev_close})
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
